import { MoneyValueDto } from '@/types/MoneyValueDto.ts';
import { PersonTargetType, TargetType } from '@/types/TargetType.ts';
import { fullNameOf } from '@/utils/personName.ts';
import { BookingBill } from '@/features/dashboard/bookingChanges/types/BookingBill.ts';
import { BookingChangeDetailDto } from '@/features/dashboard/bookingChanges/types/dto/BookingChangeDetailDto.ts';
import { BookingChangeStatus } from '@/features/dashboard/bookingChanges/types/dto/BookingChangeStatus.ts';
import {
  BasePriceChangeItemDto,
  ChangeItemDto,
  ChangeItemType,
  isChangeItemOfType,
} from '@/features/dashboard/bookingChanges/types/dto/ChangeItemDto.ts';
import { PatchBookingChangeRequest } from '@/features/dashboard/bookingChanges/types/dto/PatchBookingChangeRequest.ts';
import { PersonIdentityVm } from '@/features/dashboard/bookingChanges/types/BillViewModel.ts';
import {
  ChangeDraftVm,
  ChangePackageStatus,
  DraftImpactEntryVm,
} from '@/features/dashboard/bookingChanges/types/ChangeViewModel.ts';
import {
  buildPersonIdentity,
  CLIENT_ROLE_DETAIL,
} from '@/features/dashboard/bookingChanges/types/BillViewModel.utils.ts';
import {
  ChangePackageSummaryVm,
  ChangeSummaryGroupVm,
  ClientAcceptanceVm,
} from '@/features/dashboard/bookingChanges/types/ChangePackageFlowViewModel.ts';
import { TourConsentDto } from '@/features/dashboard/tourConsents/types/TourConsentDto.ts';
import { tourConsentDocumentsMapper } from '@/features/dashboard/bookingChanges/types/TourConsentDto.mapper.ts';
import {
  BOOKING_WIDE_KEY,
  ChangePreview,
  previewOwnParticipantDelta,
} from '@/features/dashboard/bookingChanges/types/ChangePreview.ts';
import {
  BOOKING_WIDE_GROUP_HINT,
  BOOKING_WIDE_GROUP_LABEL,
  buildChangeSummaryGroups,
} from '@/features/dashboard/bookingChanges/utils/changeSummary.ts';
import {
  bookingWideDiscountDescriptions,
  countItemsForPerson,
  manualLineItems,
} from '@/features/dashboard/bookingChanges/utils/draftItems.ts';
import { bookingBillMapper } from '@/features/dashboard/bookingChanges/types/BookingBill.mapper.ts';
import { formatDateRange } from '@/features/dashboard/bookingChanges/utils/formatters.ts';
import {
  isZeroMoneyList,
  moneyListDirection,
} from '@/features/dashboard/bookingChanges/utils/money.ts';
import { pluralizeYears } from '@/features/dashboard/bookingChanges/utils/plurals.ts';

/**
 * Mapper paczki zmian: szkic w panelu bocznym, podsumowanie kroku W1 oraz
 * złożenie żądania PATCH.
 *
 * Kwot nie liczy: kwota paczki i rozbicie na osoby przychodzą z podglądu
 * (`ChangePreview`), zbudowanego z `GET …/changes/{changeId}/preview` —
 * listami po jednej pozycji na walutę i tak też trafiają do modelu widoku.
 * `preview === null` znaczy „kwot nie znamy” — podgląd odmawia paczce
 * zamkniętej i potrafi rzucić walidacją transportu.
 */

const PACKAGE_STATUS: Record<BookingChangeStatus, ChangePackageStatus> = {
  [BookingChangeStatus.DRAFT]: ChangePackageStatus.DRAFT,
  [BookingChangeStatus.PENDING_CLIENT]: ChangePackageStatus.AWAITING_CLIENT,
  [BookingChangeStatus.APPLIED]: ChangePackageStatus.APPLIED,
  // Paczka odrzucona przez klienta i paczka zdjęta przez operatora to dwa różne
  // zdarzenia — zwinięcie ich w „Anulowana” mówiło operatorowi nieprawdę.
  [BookingChangeStatus.REJECTED]: ChangePackageStatus.REJECTED,
  [BookingChangeStatus.CANCELLED]: ChangePackageStatus.CANCELLED,
};

export const toChangePackageStatus = (
  status: BookingChangeStatus,
): ChangePackageStatus => PACKAGE_STATUS[status];

/**
 * Zakres zmian zapisującego doklejany do jego nazwiska („Kajetan Bobstwe ·
 * dane kontaktowe”).
 *
 * Bez dopisku wiersz „bez wpływu” w karcie „Wpływ paczki” i w modalu wyjścia
 * z edycji nie mówi, czego właściwie dotyczy. Nazwy pozycji bierzemy z tego
 * samego zestawienia, na którym stoi podsumowanie paczki, żeby obie listy
 * mówiły jednym słownictwem.
 */
const clientScopeLabel = (bill: BookingBill, items: ChangeItemDto[]): string =>
  buildChangeSummaryGroups(bill, items)
    .filter(
      (group) =>
        group.scope === 'person' && group.personKind === TargetType.CLIENT,
    )
    .flatMap((group) => group.items.map((item) => item.label.toLowerCase()))
    .join(', ');

const impactByPerson = (
  bill: BookingBill,
  items: ChangeItemDto[],
  preview: ChangePreview | null,
): DraftImpactEntryVm[] => {
  const bookingWideItems = manualLineItems(items, null);
  const bookingWideDescriptions = bookingWideDiscountDescriptions(items);
  // Uczestnik, który dostał wyłącznie udział w zniżce całej rezerwacji, nie ma
  // tu wiersza: po odjęciu udziału jego delta jest zerowa, a sam udział stoi
  // w kwocie wiersza „Cała rezerwacja”.
  const entries: DraftImpactEntryVm[] = bill.participants
    .filter(
      (participant) =>
        countItemsForPerson(items, participant.participantId) > 0,
    )
    .map((participant) => {
      const delta = previewOwnParticipantDelta(
        preview,
        participant.participantId,
        bookingWideDescriptions,
      );
      return {
        personId: participant.participantId,
        label: `${participant.firstName} ${participant.lastName}`,
        delta,
        withoutImpact: isZeroMoneyList(delta) || undefined,
      };
    });

  if (countItemsForPerson(items, null) > 0) {
    const scope = clientScopeLabel(bill, items);
    entries.push({
      personId: bill.client.clientId,
      label: `${bill.client.firstName} ${bill.client.lastName}${scope ? ` · ${scope}` : ''}`,
      // Zmiana danych zapisującego nigdy nie rusza kwot — rachunek ma tylko
      // uczestnik.
      delta: [],
      withoutImpact: true,
    });
  }

  if (bookingWideItems.length > 0) {
    const delta =
      buildChangeSummaryGroups(bill, items, preview).find(
        (group) => group.scope === 'booking',
      )?.totalDelta ?? [];
    entries.push({
      personId: BOOKING_WIDE_KEY,
      label: BOOKING_WIDE_GROUP_LABEL,
      delta,
      withoutImpact: isZeroMoneyList(delta) || undefined,
    });
  }

  return entries;
};

const groupIdentity = (
  personId: string,
  firstName: string,
  lastName: string,
  personKind: PersonTargetType,
  age: number | null,
): PersonIdentityVm =>
  buildPersonIdentity({
    id: personId,
    kind: personKind,
    firstName,
    lastName,
    roleDetail:
      personKind === TargetType.CLIENT
        ? CLIENT_ROLE_DETAIL
        : age === null
          ? null
          : pluralizeYears(age),
  });

/** Autor paczki jako jeden napis; audyt bywa pusty, więc `null` jest w kontrakcie. */
const authorNameOf = (detail: BookingChangeDetailDto): string | null =>
  detail.author
    ? fullNameOf(detail.author.firstName, detail.author.lastName)
    : null;

/**
 * Pozycje paczki zebrane pod osobami i pod całą rezerwacją — wspólne wejście
 * kroku W1 i podglądu strony klienta (W3). Oba kroki opisują tę samą paczkę,
 * więc lista musi być dosłownie tą samą, a nie dwoma równoległymi zestawieniami.
 */
const summaryGroups = (
  bill: BookingBill,
  items: ChangeItemDto[],
  preview: ChangePreview | null,
): ChangeSummaryGroupVm[] =>
  buildChangeSummaryGroups(bill, items, preview).map((group) => {
    const rows = group.items.map((item) => ({
      id: item.id,
      label: item.label,
      before: item.before,
      after: item.after,
      delta: item.delta,
    }));

    if (group.scope === 'booking') {
      return {
        id: BOOKING_WIDE_KEY,
        person: null,
        title: BOOKING_WIDE_GROUP_LABEL,
        roleLabel: BOOKING_WIDE_GROUP_HINT,
        clientRoleLabel: BOOKING_WIDE_GROUP_HINT,
        totalDelta: group.totalDelta,
        items: rows,
      };
    }

    const person = groupIdentity(
      group.personId,
      group.firstName,
      group.lastName,
      group.personKind,
      group.age,
    );
    return {
      id: person.id,
      person,
      title: person.fullName,
      roleLabel: person.roleLabel,
      clientRoleLabel:
        group.personKind === TargetType.CLIENT
          ? 'Ty · zapisujący'
          : 'uczestnik',
      totalDelta: group.totalDelta,
      items: rows,
    };
  });

/**
 * Czego dotyczą zmiany, w drugiej osobie — „Bobin, Marcel, całej rezerwacji
 * oraz Twoich danych”. Zapisujący czyta tę stronę o sobie, więc jego grupa nie
 * jest wymieniana z imienia.
 */
export const clientFacingScopeLabel = (
  groups: ChangeSummaryGroupVm[],
): string => {
  const participants = groups
    .filter((group) => group.person && group.person.kind !== TargetType.CLIENT)
    .map((group) => group.title.split(' ')[0])
    .join(', ');
  const segments = [
    participants,
    groups.some((group) => group.person === null) && 'całej rezerwacji',
    groups.some((group) => group.person?.kind === TargetType.CLIENT) &&
      'Twoich danych',
  ].filter((segment): segment is string => !!segment);

  if (segments.length <= 1) {
    return segments[0] ?? '';
  }
  return `${segments.slice(0, -1).join(', ')} oraz ${segments[segments.length - 1]}`;
};

/**
 * Jak różnica wchodzi w harmonogram. Rozkład jest jeden, bo backend zna jeden
 * (`SchedulePlan`): proporcjonalnie do wysokości rat nieopłaconych, bez
 * ruszania terminów — zmienia się sam rzeczownik, żeby paczka oddająca
 * pieniądze nie mówiła klientowi o dopłacie. Kwoty rat klientowi tu nie
 * pokazujemy — na jego stronie stoi sam bilans.
 */
const settlementDescriptionOf = (balance: MoneyValueDto[]): string => {
  const noun =
    moneyListDirection(balance) === -1
      ? 'Zwrot zostanie rozłożony'
      : 'Dopłata zostanie rozłożona';
  return `${noun} na raty jeszcze nieopłacone, proporcjonalnie do ich wysokości. Terminy pozostają bez zmian.`;
};

/** Pozycja ceny bazowej z wypełnioną kwotą — nadpisanie bez kwoty nie ma sensu. */
const hasPriceOverride = (
  item: BasePriceChangeItemDto,
): item is BasePriceChangeItemDto & { priceOverride: MoneyValueDto } =>
  item.priceOverride !== null;

export const bookingChangeDetailMapper = {
  /** Otwarty szkic: panel boczny, belka nagłówka i modal wyjścia z edycji. */
  toDraftVm(
    detail: BookingChangeDetailDto,
    bill: BookingBill,
    preview: ChangePreview | null = null,
  ): ChangeDraftVm {
    return {
      id: detail.id,
      bookingId: detail.bookingId,
      itemCount: detail.items.length,
      author: authorNameOf(detail),
      lastSavedAt: detail.lastModifiedAt,
      totalDelta: preview?.total ?? [],
      bookingValueAfter: bookingBillMapper.toBookingValueAfterDraft(
        bill,
        preview,
      ),
      impactByPerson: impactByPerson(bill, detail.items, preview),
    };
  },

  /** Krok W1 — podsumowanie tego, co zmienia szkic. */
  toSummaryVm(
    detail: BookingChangeDetailDto,
    bill: BookingBill,
    packageNumber: number,
    preview: ChangePreview | null = null,
  ): ChangePackageSummaryVm {
    const groups = summaryGroups(bill, detail.items, preview);
    const allItems = groups.flatMap((group) => group.items);

    return {
      packageNumber,
      status: toChangePackageStatus(detail.status),
      bookingId: detail.bookingId,
      bookingCode: bill.bookingCode,
      groups,
      balance: preview?.total ?? [],
      itemCount: detail.items.length,
      financialItemCount: allItems.filter(
        (item) => !isZeroMoneyList(item.delta),
      ).length,
    };
  },

  /**
   * Krok W3 — podgląd strony, którą po wysłaniu zobaczy osoba zapisująca.
   *
   * Składany **z danych operatora**, nie z odczytu publicznego: token
   * rezerwacji powstaje wyłącznie w szablonie wiadomości, więc panel operatora
   * nie ma czym zawołać `GET …/booking-summary/change`. Pozycje, wartości
   * „przed → po” i kwoty są tu dosłownie tymi samymi, na których stoi krok W1,
   * więc podgląd nie może rozjechać się z paczką, którą klient dostanie.
   *
   * Czego podgląd nie pokazuje, bo nie ma tego w żadnym kontrakcie: terminu
   * ważności linku. Paczka czekająca na klienta nie wygasa sama — token
   * rezerwacji żyje do końca wyjazdu, a paczkę zdejmuje operator.
   */
  toClientAcceptanceVm(
    detail: BookingChangeDetailDto,
    bill: BookingBill,
    consents: TourConsentDto[],
    preview: ChangePreview | null = null,
  ): ClientAcceptanceVm {
    const groups = summaryGroups(bill, detail.items, preview);

    return {
      bookingCode: bill.bookingCode,
      tourName: bill.tourName,
      dateRangeLabel: formatDateRange(bill.tourStartDate, bill.tourEndDate),
      scopeLabel: clientFacingScopeLabel(groups),
      groups,
      // Kwota z samej paczki, a nie z podglądu: to ona pojedzie klientowi
      // jako `additionalPayment` i to ją potwierdzi przy akceptacji.
      balance: detail.financialDelta,
      settlementDescription: settlementDescriptionOf(detail.financialDelta),
      documents: tourConsentDocumentsMapper.toDocuments(
        consents.filter((consent) =>
          detail.amendmentTourConsentIds.includes(consent.id),
        ),
      ),
      // Wysłanie linku to chwila rozwiązania paczki; `null`, dopóki operator
      // jej nie wysłał.
      sentAt: detail.resolvedAt,
    };
  },

  /**
   * Żądanie PATCH z pełnej listy pozycji szkicu.
   *
   * PATCH podmienia całą zawartość paczki, więc wejściem musi być komplet
   * pozycji **po** zmianie, a nie sama różnica. Typowe użycie: wziąć
   * `detail.items`, podmienić/dorzucić/usunąć jedną pozycję i wysłać całość.
   */
  toPatchRequest(items: ChangeItemDto[]): PatchBookingChangeRequest {
    const clientItem = items.find(
      isChangeItemOfType(ChangeItemType.CLIENT_DATA),
    );

    const participantDataChanges = items
      .filter(isChangeItemOfType(ChangeItemType.PARTICIPANT_DATA))
      .map((item) => ({
        participantId: item.participantId ?? '',
        firstName: item.newFirstName,
        lastName: item.newLastName,
        birthDate: item.newBirthDate,
      }));

    const optionChanges = items
      .filter(isChangeItemOfType(ChangeItemType.OPTION))
      .map((item) => ({
        participantId: item.participantId ?? '',
        op: item.op,
        tourOptionId: item.tourOptionId,
        removedVariantId: item.removedVariantId,
        newVariantId: item.newVariantId,
        // Próg cenowy wariantu rozwiązuje backend po wieku uczestnika — front nie ma
        // po czym go odtworzyć, bo odpowiedź `newPriceId` nie niesie.
        newPriceId: null,
        priceSource: item.priceSource,
        priceOverride: item.priceOverride,
        priceNote: item.priceNote,
      }));

    const customValuesChanges = items
      .filter(isChangeItemOfType(ChangeItemType.CUSTOM_VALUES))
      .map((item) => ({
        participantId: item.participantId ?? '',
        values: item.newCustomValues ?? {},
      }));

    const transportChanges = items
      .filter(isChangeItemOfType(ChangeItemType.TRANSPORT))
      .map((item) => ({
        participantId: item.participantId ?? '',
        op: item.op,
        previousVariantId: item.previousVariantId,
        newVariantId: item.newVariantId,
        priceSource: item.priceSource,
        priceOverride: item.priceOverride,
        priceNote: item.priceNote,
      }));

    // Żądanie wymaga kwoty, a odpowiedź dopuszcza jej brak — predykat zawęża
    // typ, żeby zejść z wykrzyknika.
    const basePriceChanges = items
      .filter(isChangeItemOfType(ChangeItemType.BASE_PRICE))
      .filter(hasPriceOverride)
      .map((item) => ({
        participantId: item.participantId ?? '',
        priceOverride: item.priceOverride,
        priceNote: item.priceNote,
      }));

    const insuranceChanges = items
      .filter(isChangeItemOfType(ChangeItemType.INSURANCE))
      .map((item) => ({
        participantId: item.participantId ?? '',
        op: item.op,
        previousInsuranceId: item.previousInsuranceId,
        newInsuranceId: item.newInsuranceId,
      }));

    // Jedyna sekcja, w której `participantId` zostaje `null`: pozycja bez
    // uczestnika dotyczy całej rezerwacji, a nie osoby (reguła D10). Pozostałe
    // sekcje podstawiają w tym miejscu pusty napis, bo ich kontrakt uczestnika
    // wymaga.
    const manualLineChanges = items
      .filter(isChangeItemOfType(ChangeItemType.MANUAL_LINE))
      .map((item) => ({
        participantId: item.participantId,
        description: item.description,
        priceOverride: item.priceOverride,
        priceNote: item.priceNote,
      }));

    return {
      participantDataChanges,
      optionChanges,
      clientDataChange: clientItem
        ? {
            firstName: clientItem.newFirstName,
            lastName: clientItem.newLastName,
            email: clientItem.newEmail,
            phoneNumber: clientItem.newPhoneNumber,
            birthDate: clientItem.newBirthDate,
            address: clientItem.newAddress,
          }
        : null,
      customValuesChanges,
      transportChanges,
      basePriceChanges,
      insuranceChanges,
      manualLineChanges,
    };
  },
};
